// engagement-components/EngagementDashboard.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaArrowRight,
  FaBullhorn,
  FaChartLine,
  FaCheckCircle,
  FaEnvelope,
  FaExclamationCircle,
  FaMousePointer,
  FaPaperPlane,
  FaPlus,
  FaRedoAlt,
  FaRoute,
  FaRupeeSign,
  FaSms,
  FaWhatsapp,
} from "react-icons/fa";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { apiUrl, authHeaders } from "./config";

dayjs.extend(relativeTime);

const C = {
  brand: "#4f46e5",
  brandDark: "#3730a3",
  brandLt: "#eef2ff",
  green: "#059669",
  greenLt: "#ecfdf5",
  amber: "#d97706",
  amberLt: "#fffbeb",
  rose: "#be185d",
  roseLt: "#fdf2f8",
  cyan: "#0891b2",
  cyanLt: "#ecfeff",
  violet: "#7c3aed",
  violetLt: "#f5f3ff",
  text: "#0f172a",
  muted: "#64748b",
  subtle: "#94a3b8",
  border: "#e2e8f0",
  bg: "#f8fafc",
  white: "#ffffff",
};

const statusColors = {
  Active: { bg: "#ecfdf5", color: "#047857", border: "#bbf7d0" },
  Paused: { bg: "#f8fafc", color: "#64748b", border: "#e2e8f0" },
  Completed: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  Scheduled: { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
  Draft: { bg: "#f8fafc", color: "#94a3b8", border: "#e2e8f0" },
};

const normaliseStatus = (status) => {
  const value = String(status || "Draft").trim();
  return value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : "Draft";
};

const toNumber = (value) => Number(value || 0) || 0;

const compactNumber = (value) =>
  toNumber(value).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const formatRate = (value) => `${toNumber(value).toFixed(1).replace(".0", "")}%`;

const formatRevenue = (value) => {
  const amount = toNumber(value);
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${compactNumber(amount)}`;
};

const StatusPill = ({ status }) => {
  const safeStatus = normaliseStatus(status);
  const s = statusColors[safeStatus] || statusColors.Draft;

  return (
    <span
      className="eng-status-pill"
      style={{ "--status-bg": s.bg, "--status-color": s.color, "--status-border": s.border }}
    >
      <span className="eng-status-dot" />
      {safeStatus}
    </span>
  );
};

const ChannelIcon = ({ channel }) => {
  if (channel === "WhatsApp") return <FaWhatsapp className="eng-channel-icon whatsapp" />;
  if (channel === "SMS") return <FaSms className="eng-channel-icon sms" />;
  return <FaEnvelope className="eng-channel-icon email" />;
};

const STAT_CONFIG = [
  {
    key: "totalSent",
    label: "Messages Sent",
    fmt: compactNumber,
    icon: <FaPaperPlane />,
    tone: "#4f46e5",
  },
  {
    key: "deliveryRate",
    label: "Delivery Rate",
    fmt: formatRate,
    icon: <FaCheckCircle />,
    tone: "#059669",
  },
  {
    key: "clickRate",
    label: "Click Rate",
    fmt: formatRate,
    icon: <FaMousePointer />,
    tone: "#0891b2",
  },
  {
    key: "conversions",
    label: "Conversions",
    fmt: compactNumber,
    icon: <FaChartLine />,
    tone: "#d97706",
  },
  {
    key: "revenue",
    label: "Revenue",
    fmt: formatRevenue,
    icon: <FaRupeeSign />,
    tone: "#be185d",
  },
  {
    key: "activeJourneys",
    label: "Active Journeys",
    fmt: compactNumber,
    icon: <FaRoute />,
    tone: "#7c3aed",
  },
];

const FUNNEL_COLORS = ["#4f46e5", "#0891b2", "#059669", "#d97706"];

const EmptyState = ({ icon, title, copy, actionLabel, onAction }) => (
  <div className="eng-empty-state">
    <div className="eng-empty-icon">{icon}</div>
    <strong>{title}</strong>
    <p>{copy}</p>
    {actionLabel ? (
      <button type="button" className="eng-primary-btn" onClick={onAction}>
        <FaPlus size={11} />
        {actionLabel}
      </button>
    ) : null}
  </div>
);

const EngagementDashboard = ({ onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [journeys, setJourneys] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedRef = useRef(false);

  const fetchAll = useCallback(async (manualRefresh = false) => {
    if (manualRefresh) {
      setRefreshing(true);
    } else if (!hasLoadedRef.current) {
      setLoading(true);
    }

    try {
      const [jRes, cRes] = await Promise.all([
        axios.get(`${apiUrl}/feedback/journeys`, authHeaders()),
        axios.get(`${apiUrl}/feedback/campaigns`, authHeaders()),
      ]);

      const journeyData = (jRes.data || []).map((j) => ({
        ...j,
        id: j.journeyid || j.id,
        status: normaliseStatus(j.status),
        trigger: j.trigger_type || j.trigger || "Manual",
        steps: Array.isArray(j.steps_data) ? j.steps_data.length : toNumber(j.steps || 0),
      }));

      const campaignData = (cRes.data || []).map((c) => ({
        ...c,
        id: c.campaignid || c.id,
        status: normaliseStatus(c.status),
        channel: c.fallback?.channels?.[0] || c.channel || "WhatsApp",
        audience: toNumber(c.audience_count || c.audience),
        sent: toNumber(c.sent),
        delivered: toNumber(c.delivered),
        clicked: toNumber(c.clicked),
        conversions: toNumber(c.converted || c.conversions),
        revenue: toNumber(c.revenue_actual || c.revenue),
        createdAt: c.created_at || c.createdAt,
      }));

      setJourneys(journeyData.slice(0, 4));
      setCampaigns(campaignData.slice(0, 5));

      const totalSent = campaignData.reduce((s, c) => s + toNumber(c.sent), 0);
      const totalDelivered = campaignData.reduce((s, c) => s + toNumber(c.delivered), 0);
      const totalClicked = campaignData.reduce((s, c) => s + toNumber(c.clicked), 0);
      const totalConverted = campaignData.reduce((s, c) => s + toNumber(c.conversions), 0);
      const totalRevenue = campaignData.reduce((s, c) => s + toNumber(c.revenue), 0);

      setStats({
        totalSent,
        totalDelivered,
        totalClicked,
        deliveryRate: totalSent > 0 ? (totalDelivered / totalSent) * 100 : 0,
        clickRate: totalDelivered > 0 ? (totalClicked / totalDelivered) * 100 : 0,
        conversions: totalConverted,
        revenue: totalRevenue,
        activeJourneys: journeyData.filter((j) => j.status === "Active").length,
      });
    } catch (error) {
      console.error("engagement dashboard fetch error:", error);
      setStats({
        totalSent: 0,
        totalDelivered: 0,
        totalClicked: 0,
        deliveryRate: 0,
        clickRate: 0,
        conversions: 0,
        revenue: 0,
        activeJourneys: 0,
      });
      setJourneys([]);
      setCampaigns([]);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(false);
  }, [fetchAll]);

  const funnel = useMemo(
    () => [
      { stage: "Sent", count: stats?.totalSent || 0 },
      { stage: "Delivered", count: stats?.totalDelivered || 0 },
      { stage: "Clicked", count: stats?.totalClicked || 0 },
      { stage: "Converted", count: stats?.conversions || 0 },
    ],
    [stats]
  );

  const maxCount = funnel[0].count || 1;
  const topCampaign = campaigns[0];

  if (loading) {
    return (
      <div className="eng-dashboard-shell">
        <style>{dashboardStyles}</style>
        <div className="eng-dashboard-loader">
          <div className="eng-loader-card">
            <div className="eng-loader-dots">
              <span />
              <span />
              <span />
              <span />
            </div>
            <strong>Loading engagement dashboard...</strong>
            <p>Preparing campaigns, journeys, conversion signals, and guest communication performance.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="eng-dashboard-shell">
      <style>{dashboardStyles}</style>

      <section className="eng-hero">
        <div className="eng-hero-left">
          <span className="eng-hero-icon">
            <FaBullhorn />
          </span>

          <div>
            <div className="eng-eyebrow">Guest Engagement Control</div>
            <h2>Engagement Dashboard</h2>
            <p>
              Monitor campaigns, automated guest journeys, engagement funnel performance, and direct revenue signals from one workspace.
            </p>

            <div className="eng-hero-chips">
              <span>{compactNumber(stats?.totalSent)} messages sent</span>
              <span>{formatRate(stats?.deliveryRate)} delivered</span>
              <span>{compactNumber(stats?.activeJourneys)} active journeys</span>
            </div>
          </div>
        </div>

        <div className="eng-hero-actions">
          <button type="button" className="eng-soft-btn" onClick={() => fetchAll(true)} disabled={refreshing}>
            <FaRedoAlt className={refreshing ? "eng-spin" : ""} size={12} />
            {refreshing ? "Refreshing" : "Refresh"}
          </button>

          <button type="button" className="eng-outline-btn" onClick={() => onNavigate("journeys-create")}>
            <FaPlus size={11} />
            Journey
          </button>

          <button type="button" className="eng-primary-btn" onClick={() => onNavigate("campaigns-create")}>
            <FaPlus size={11} />
            Campaign
          </button>
        </div>
      </section>

      <section className="eng-stat-grid">
        {STAT_CONFIG.map((s) => (
          <article className="eng-stat-card" style={{ "--tone": s.tone }} key={s.key}>
            <div className="eng-stat-top">
              <span>{s.label}</span>
              <span className="eng-stat-icon">{s.icon}</span>
            </div>
            <strong>{s.fmt(stats?.[s.key])}</strong>
          </article>
        ))}
      </section>

      <section className="eng-main-grid">
        <article className="eng-panel">
          <div className="eng-panel-head">
            <div>
              <div className="eng-eyebrow">Funnel Quality</div>
              <h3>Engagement Funnel</h3>
              <p>Track guest progress from delivery to click and conversion.</p>
            </div>
          </div>

          <div className="eng-panel-body">
            {funnel.map((item, i) => {
              const pct = (item.count / maxCount) * 100;
              const conversionPct = i > 0 && maxCount > 0 ? (item.count / maxCount) * 100 : 100;

              return (
                <div className="eng-funnel-row" key={item.stage}>
                  <div className="eng-funnel-label">
                    <span>{item.stage}</span>
                    <strong style={{ color: FUNNEL_COLORS[i] }}>
                      {compactNumber(item.count)}
                      {i > 0 ? <em>{conversionPct.toFixed(1)}%</em> : null}
                    </strong>
                  </div>

                  <div className="eng-funnel-track">
                    <span
                      style={{
                        width: `${Math.max(item.count > 0 ? 5 : 0, pct)}%`,
                        background: `linear-gradient(90deg, ${FUNNEL_COLORS[i]}, ${FUNNEL_COLORS[i]}aa)`,
                      }}
                    />
                  </div>
                </div>
              );
            })}

            {funnel[0].count === 0 ? (
              <div className="eng-funnel-empty">Send a campaign to start building funnel data.</div>
            ) : null}
          </div>
        </article>

        <article className="eng-panel">
          <div className="eng-panel-head">
            <div>
              <div className="eng-eyebrow">Automation</div>
              <h3>Active Journeys</h3>
              <p>Latest automated guest journeys and delivery health.</p>
            </div>

            <button type="button" className="eng-link-btn" onClick={() => onNavigate("journeys")}>
              View all <FaArrowRight size={10} />
            </button>
          </div>

          <div className="eng-panel-body">
            {journeys.length === 0 ? (
              <EmptyState
                icon="🔁"
                title="No journeys yet"
                copy="Automate birthday wishes, check-in messages, anniversary greetings and follow-ups."
                actionLabel="Create First Journey"
                onAction={() => onNavigate("journeys-create")}
              />
            ) : (
              <div className="eng-journey-grid">
                {journeys.map((j) => {
                  const isActive = j.status === "Active";

                  return (
                    <button
                      type="button"
                      key={j.id}
                      className={`eng-journey-card ${isActive ? "is-active" : ""}`}
                      onClick={() => onNavigate("journeys-edit", j)}
                    >
                      <div className="eng-journey-top">
                        <strong title={j.name}>{j.name}</strong>
                        <StatusPill status={j.status} />
                      </div>

                      <div className="eng-journey-meta">
                        <span>
                          <i className="fas fa-bolt" />
                          {j.trigger}
                        </span>
                        <span>{j.steps} step{j.steps === 1 ? "" : "s"}</span>
                      </div>

                      <div className="eng-journey-metrics">
                        <span>
                          <em>Fired</em>
                          <strong>{compactNumber(j.total_fired)}</strong>
                        </span>
                        <span>
                          <em>Delivered</em>
                          <strong>{compactNumber(j.total_delivered)}</strong>
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </article>
      </section>

      <section className="eng-campaign-panel">
        <div className="eng-panel-head">
          <div>
            <div className="eng-eyebrow">Campaign Performance</div>
            <h3>Recent Campaigns</h3>
            <p>
              {topCampaign
                ? `Latest campaign: ${topCampaign.name}`
                : "Broadcasts and promotional campaigns will appear here."}
            </p>
          </div>

          <div className="eng-campaign-actions">
            <button type="button" className="eng-link-btn" onClick={() => onNavigate("campaigns")}>
              View all <FaArrowRight size={10} />
            </button>
            <button type="button" className="eng-primary-btn sm" onClick={() => onNavigate("campaigns-create")}>
              <FaPlus size={10} />
              New
            </button>
          </div>
        </div>

        {campaigns.length === 0 ? (
          <EmptyState
            icon="📣"
            title="No campaigns yet"
            copy="Send your first broadcast to guests and start driving direct engagement and revenue."
            actionLabel="Create Campaign"
            onAction={() => onNavigate("campaigns-create")}
          />
        ) : (
          <div className="eng-table-wrap">
            <table className="eng-campaign-table">
              <thead>
                <tr>
                  {["Campaign", "Channel", "Audience", "Sent", "Clicked", "Conversions", "Revenue", "Status"].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} onClick={() => onNavigate("campaigns-edit", c)}>
                    <td>
                      <strong>{c.name}</strong>
                      <span>{c.createdAt ? `${dayjs(c.createdAt).format("DD MMM YYYY")} · ${dayjs(c.createdAt).fromNow()}` : "—"}</span>
                    </td>
                    <td>
                      <span className="eng-channel-pill">
                        <ChannelIcon channel={c.channel} />
                        {c.channel}
                      </span>
                    </td>
                    <td>{compactNumber(c.audience)}</td>
                    <td>{compactNumber(c.sent)}</td>
                    <td className="is-positive">{compactNumber(c.clicked)}</td>
                    <td className="is-brand">{compactNumber(c.conversions)}</td>
                    <td className="is-positive">{formatRevenue(c.revenue)}</td>
                    <td>
                      <StatusPill status={c.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

const dashboardStyles = `
  .eng-dashboard-shell {
    min-height: 100vh;
    padding: 18px;
    background:
      radial-gradient(circle at 8% 0%, rgba(79, 70, 229, 0.08), transparent 30%),
      radial-gradient(circle at 92% 2%, rgba(5, 150, 105, 0.08), transparent 28%),
      ${C.bg};
    color: ${C.text};
  }

  .eng-dashboard-loader {
    min-height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .eng-loader-card {
    width: min(460px, 92vw);
    background: ${C.white};
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 24px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.08);
    padding: 30px;
    text-align: center;
  }

  .eng-loader-card strong {
    display: block;
    color: ${C.text};
    font-size: 0.96rem;
    font-weight: 900;
  }

  .eng-loader-card p {
    color: ${C.muted};
    font-size: 0.8rem;
    font-weight: 650;
    line-height: 1.5;
    margin: 8px 0 0;
  }

  .eng-loader-dots {
    display: flex;
    gap: 8px;
    justify-content: center;
    margin-bottom: 16px;
  }

  .eng-loader-dots span {
    width: 10px;
    height: 10px;
    background: ${C.brand};
    border-radius: 50%;
    animation: eng-bounce 0.58s infinite alternate;
  }

  .eng-loader-dots span:nth-child(2) { animation-delay: 0.1s; }
  .eng-loader-dots span:nth-child(3) { animation-delay: 0.2s; }
  .eng-loader-dots span:nth-child(4) { animation-delay: 0.3s; }

  @keyframes eng-bounce {
    to { transform: translateY(-10px); }
  }

  @keyframes eng-spin {
    to { transform: rotate(360deg); }
  }

  .eng-spin {
    animation: eng-spin 0.8s linear infinite;
  }

  .eng-hero {
    background:
      radial-gradient(circle at top left, rgba(79, 70, 229, 0.14), transparent 34%),
      radial-gradient(circle at top right, rgba(5, 150, 105, 0.10), transparent 32%),
      linear-gradient(135deg, #ffffff 0%, #f8fbff 100%);
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 24px;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.075);
    padding: 18px;
    margin-bottom: 14px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }

  .eng-hero-left {
    display: flex;
    align-items: flex-start;
    gap: 13px;
    min-width: 0;
  }

  .eng-hero-icon {
    width: 54px;
    height: 54px;
    border-radius: 19px;
    background: linear-gradient(135deg, ${C.brandDark}, ${C.brand});
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 16px 34px rgba(79, 70, 229, 0.22);
    flex: 0 0 auto;
    font-size: 1.35rem;
  }

  .eng-eyebrow {
    color: ${C.brand};
    font-size: 0.72rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-weight: 950;
    margin-bottom: 5px;
  }

  .eng-hero h2,
  .eng-panel-head h3 {
    color: #001f5b;
    font-weight: 950;
    margin: 0;
  }

  .eng-hero h2 {
    font-size: 1.48rem;
    line-height: 1.14;
  }

  .eng-panel-head h3 {
    font-size: 1.05rem;
    line-height: 1.18;
  }

  .eng-hero p,
  .eng-panel-head p {
    color: ${C.muted};
    font-size: 0.8rem;
    line-height: 1.5;
    font-weight: 700;
    margin: 7px 0 0;
  }

  .eng-hero p {
    max-width: 820px;
  }

  .eng-hero-chips {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 12px;
  }

  .eng-hero-chips span {
    min-height: 28px;
    border-radius: 999px;
    padding: 5px 10px;
    background: #ffffff;
    color: #334155;
    border: 1px solid rgba(15, 23, 42, 0.08);
    font-size: 0.72rem;
    font-weight: 900;
  }

  .eng-hero-actions,
  .eng-campaign-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .eng-primary-btn,
  .eng-outline-btn,
  .eng-soft-btn,
  .eng-link-btn {
    min-height: 40px;
    border-radius: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 0 15px;
    font-size: 0.8rem;
    font-weight: 950;
    cursor: pointer;
    white-space: nowrap;
    transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
  }

  .eng-primary-btn:hover,
  .eng-outline-btn:hover,
  .eng-soft-btn:hover,
  .eng-link-btn:hover {
    transform: translateY(-1px);
  }

  .eng-primary-btn {
    border: 1px solid ${C.brand};
    background: linear-gradient(135deg, ${C.brandDark}, ${C.brand});
    color: #ffffff;
    box-shadow: 0 14px 30px rgba(79, 70, 229, 0.20);
  }

  .eng-primary-btn.sm {
    min-height: 34px;
    border-radius: 12px;
    padding: 0 12px;
    font-size: 0.74rem;
  }

  .eng-outline-btn {
    border: 1px solid rgba(79, 70, 229, 0.30);
    background: #ffffff;
    color: ${C.brand};
  }

  .eng-soft-btn {
    border: 1px solid rgba(15, 23, 42, 0.10);
    background: #ffffff;
    color: #334155;
  }

  .eng-soft-btn:disabled {
    opacity: 0.7;
    cursor: wait;
  }

  .eng-link-btn {
    min-height: 34px;
    padding: 0;
    border: 0;
    background: transparent;
    color: ${C.brand};
    box-shadow: none;
  }

  .eng-stat-grid {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 14px;
  }

  .eng-stat-card {
    background:
      radial-gradient(circle at top right, color-mix(in srgb, var(--tone, ${C.brand}) 12%, transparent), transparent 36%),
      #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-top: 4px solid var(--tone, ${C.brand});
    border-radius: 20px;
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
    padding: 14px;
    min-width: 0;
  }

  .eng-stat-top {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    align-items: center;
    margin-bottom: 10px;
  }

  .eng-stat-top > span:first-child {
    color: ${C.muted};
    font-size: 0.66rem;
    font-weight: 950;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .eng-stat-icon {
    width: 34px;
    height: 34px;
    border-radius: 13px;
    color: var(--tone, ${C.brand});
    background: color-mix(in srgb, var(--tone, ${C.brand}) 12%, white);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
  }

  .eng-stat-card strong {
    color: ${C.text};
    display: block;
    font-size: 1.28rem;
    line-height: 1.12;
    font-weight: 950;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .eng-main-grid {
    display: grid;
    grid-template-columns: minmax(300px, 0.82fr) minmax(0, 1.18fr);
    gap: 14px;
    margin-bottom: 14px;
  }

  .eng-panel,
  .eng-campaign-panel {
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 24px;
    box-shadow: 0 16px 42px rgba(15, 23, 42, 0.07);
    overflow: hidden;
  }

  .eng-panel-head {
    padding: 15px 17px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
    background:
      radial-gradient(circle at top right, rgba(79, 70, 229, 0.08), transparent 28%),
      #ffffff;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
    flex-wrap: wrap;
  }

  .eng-panel-body {
    padding: 15px;
    background: ${C.bg};
  }

  .eng-funnel-row {
    margin-bottom: 15px;
  }

  .eng-funnel-row:last-child {
    margin-bottom: 0;
  }

  .eng-funnel-label {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    margin-bottom: 6px;
  }

  .eng-funnel-label span {
    color: ${C.text};
    font-size: 0.8rem;
    font-weight: 850;
  }

  .eng-funnel-label strong {
    font-size: 0.8rem;
    font-weight: 950;
  }

  .eng-funnel-label em {
    color: ${C.subtle};
    font-style: normal;
    font-weight: 750;
    margin-left: 5px;
  }

  .eng-funnel-track {
    height: 9px;
    border-radius: 999px;
    background: #e2e8f0;
    overflow: hidden;
  }

  .eng-funnel-track span {
    display: block;
    height: 100%;
    border-radius: 999px;
    transition: width 0.65s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .eng-funnel-empty {
    text-align: center;
    margin-top: 16px;
    padding: 12px;
    border-radius: 16px;
    background: #ffffff;
    border: 1px dashed ${C.border};
    color: ${C.muted};
    font-size: 0.78rem;
    font-weight: 750;
  }

  .eng-empty-state {
    min-height: 240px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    text-align: center;
    padding: 24px;
    background: #ffffff;
    border: 1px dashed ${C.border};
    border-radius: 20px;
  }

  .eng-empty-icon {
    font-size: 2.5rem;
    line-height: 1;
  }

  .eng-empty-state strong {
    color: ${C.text};
    font-size: 0.98rem;
    font-weight: 950;
  }

  .eng-empty-state p {
    max-width: 340px;
    color: ${C.muted};
    font-size: 0.8rem;
    line-height: 1.48;
    font-weight: 700;
    margin: 0 0 8px;
  }

  .eng-journey-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  .eng-journey-card {
    width: 100%;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-top: 4px solid #94a3b8;
    border-radius: 18px;
    background: #ffffff;
    padding: 13px;
    cursor: pointer;
    text-align: left;
    transition: transform 0.16s ease, box-shadow 0.16s ease, border-color 0.16s ease;
  }

  .eng-journey-card.is-active {
    border-top-color: ${C.green};
    background:
      radial-gradient(circle at top right, rgba(5, 150, 105, 0.10), transparent 34%),
      #ffffff;
  }

  .eng-journey-card:hover {
    transform: translateY(-2px);
    border-color: #bfdbfe;
    box-shadow: 0 16px 34px rgba(15, 23, 42, 0.10);
  }

  .eng-journey-top {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 9px;
  }

  .eng-journey-top strong {
    color: ${C.text};
    font-size: 0.86rem;
    line-height: 1.25;
    font-weight: 950;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .eng-journey-meta {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    color: ${C.muted};
    font-size: 0.73rem;
    font-weight: 800;
    margin-bottom: 12px;
  }

  .eng-journey-meta span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  .eng-journey-metrics {
    display: flex;
    gap: 10px;
  }

  .eng-journey-metrics span {
    min-width: 82px;
    border-radius: 14px;
    background: ${C.bg};
    border: 1px solid rgba(15, 23, 42, 0.06);
    padding: 8px 9px;
  }

  .eng-journey-metrics em {
    display: block;
    color: ${C.subtle};
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-style: normal;
    font-weight: 950;
    margin-bottom: 2px;
  }

  .eng-journey-metrics strong {
    color: ${C.text};
    font-size: 0.86rem;
    font-weight: 950;
  }

  .eng-status-pill {
    min-height: 25px;
    border-radius: 999px;
    padding: 4px 8px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: var(--status-bg);
    color: var(--status-color);
    border: 1px solid var(--status-border);
    font-size: 0.66rem;
    font-weight: 950;
    white-space: nowrap;
  }

  .eng-status-dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: currentColor;
  }

  .eng-table-wrap {
    overflow-x: auto;
  }

  .eng-campaign-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 980px;
  }

  .eng-campaign-table th {
    background: ${C.bg};
    color: ${C.subtle};
    padding: 11px 16px;
    text-align: left;
    font-size: 0.66rem;
    font-weight: 950;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    border-bottom: 1px solid ${C.border};
    white-space: nowrap;
  }

  .eng-campaign-table th:nth-child(n+3):nth-child(-n+6) {
    text-align: center;
  }

  .eng-campaign-table th:nth-child(7) {
    text-align: right;
  }

  .eng-campaign-table td {
    padding: 14px 16px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.06);
    color: #334155;
    font-size: 0.82rem;
    font-weight: 750;
    white-space: nowrap;
  }

  .eng-campaign-table td:nth-child(n+3):nth-child(-n+6) {
    text-align: center;
  }

  .eng-campaign-table td:nth-child(7) {
    text-align: right;
  }

  .eng-campaign-table tr {
    cursor: pointer;
    transition: background 0.14s ease;
  }

  .eng-campaign-table tr:hover td {
    background: #eff6ff;
  }

  .eng-campaign-table td:first-child strong {
    display: block;
    color: ${C.text};
    font-weight: 950;
    margin-bottom: 3px;
  }

  .eng-campaign-table td:first-child span {
    display: block;
    color: ${C.subtle};
    font-size: 0.7rem;
    font-weight: 750;
  }

  .eng-channel-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 28px;
    border-radius: 999px;
    padding: 4px 9px;
    background: ${C.bg};
    border: 1px solid rgba(15, 23, 42, 0.08);
    color: ${C.text};
    font-weight: 900;
  }

  .eng-channel-icon.whatsapp { color: #25d366; }
  .eng-channel-icon.sms { color: #64748b; }
  .eng-channel-icon.email { color: #2563eb; }

  .is-positive {
    color: ${C.green} !important;
    font-weight: 950 !important;
  }

  .is-brand {
    color: ${C.brand} !important;
    font-weight: 950 !important;
  }

  @media (max-width: 1320px) {
    .eng-stat-grid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .eng-main-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 860px) {
    .eng-dashboard-shell {
      padding: 12px;
    }

    .eng-stat-grid,
    .eng-journey-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .eng-hero-actions {
      width: 100%;
      justify-content: flex-start;
    }
  }

  @media (max-width: 560px) {
    .eng-stat-grid,
    .eng-journey-grid {
      grid-template-columns: 1fr;
    }

    .eng-hero-left {
      flex-direction: column;
    }

    .eng-hero-actions,
    .eng-hero-actions button,
    .eng-campaign-actions,
    .eng-campaign-actions button {
      width: 100%;
    }
  }
`;

export default EngagementDashboard;
